import {
  createSubmissionsRepository,
  createMarketFinderRepository,
} from "../repositories/submissions.repository";
import type {
  SubmissionsRepository,
  ClassCodeSearchResult,
} from "../repositories/submissions.repository";
import type {
  Submission,
  SubmissionUpdatePayload,
  GetSubmissionsParams,
  SubmissionsResponse,
  ScheduleOfHazard,
  BuildingData,
  BuildingPayload,
  CoverLetter,
  CoverLetterRevision,
  CommunicationReviewPage,
  ConversationReviewMessage,
  CommunicationsSummary,
  ConversationThread,
  ConversationThreadMessage,
  ConversationMessageEmail,
  ConversationReplyAttachmentRef,
  MarketFinderUserPreferences,
  MarketFinderUserPreferencesUpdate,
  AimCompanyPage,
  AimContactImportInput,
  AimContactImportResult,
  AimContactPage,
  AimMarketImportInput,
  MarketFinderActorInfo,
  MarketFinderDemoAgentThreadResult,
  MarketFinderDemoConfigResult,
  MarketFinderDemoResetResult,
  MarketContact,
  MarketContactDirectoryParams,
  MarketContactDirectoryResponse,
  MarketContactInput,
  MarketCreateInput,
  MarketCreateResponse,
  MarketDirectoryItem,
  MarketsDirectoryParams,
  MarketsDirectoryResponse,
  MarketUpdateInput,
  MarketPacket,
  PossibleMatch,
  MarketPacketPreview,
  MarketPacketSendResult,
  MarketPacketUpdateInput,
  AgentEmailInput,
  PremiseData,
} from "../types/submissions.types";
import type { AuthHeaders } from "@/lib/auth/types";
import type { RenewalInformationInput } from "../validators/renewal.validator";
import type { HitRateResponse } from "../types/hit-rates.types";
import type {
  ClassCodeInput,
  GLCoverageInput,
} from "../validators/general-liability.validator";

export class SubmissionsService {
  private static instance: SubmissionsService;
  private static marketFinderInstance: SubmissionsService;

  private constructor(
    private repository: SubmissionsRepository = createSubmissionsRepository(),
  ) {}

  static getInstance(): SubmissionsService {
    if (!SubmissionsService.instance) {
      SubmissionsService.instance = new SubmissionsService();
    }
    return SubmissionsService.instance;
  }

  static getMarketFinderInstance(): SubmissionsService {
    if (!SubmissionsService.marketFinderInstance) {
      SubmissionsService.marketFinderInstance = new SubmissionsService(
        createMarketFinderRepository(),
      );
    }
    return SubmissionsService.marketFinderInstance;
  }

  async getSubmissions(
    params: GetSubmissionsParams,
  ): Promise<SubmissionsResponse> {
    const response = await this.repository.getSubmissions(params);

    return {
      ...response,
      items: response.items.map(this.enrichSubmission),
    };
  }

  async getDistinctUnderwriters(params: {
    office_ids?: number[];
    company_ids?: number[];
    show_archived?: boolean;
    headers: AuthHeaders;
  }): Promise<{ id: string; displayName: string; self?: boolean }[]> {
    return this.repository.getDistinctUnderwriters(params);
  }

  async getSubmissionById(
    id: number,
    headers: AuthHeaders,
  ): Promise<Submission> {
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    const submission = await this.repository.getSubmissionById(id, headers);
    return this.enrichSubmission(submission);
  }

  async batchAssign(
    applicationIds: number[],
    companyId: number,
    officeId: number | null,
    headers: AuthHeaders,
  ): Promise<{ updated_count: number }> {
    if (applicationIds.length === 0) {
      throw new Error("No submissions selected");
    }
    return this.repository.batchAssign(
      applicationIds,
      companyId,
      officeId,
      headers,
    );
  }

  async archiveSubmission(
    id: number,
    headers: AuthHeaders,
  ): Promise<void> {
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    await this.repository.archiveSubmission(id, true, headers);
  }

  async unarchiveSubmission(
    id: number,
    headers: AuthHeaders,
  ): Promise<void> {
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    await this.repository.unarchiveSubmission(id, headers);
  }

  async archiveMultipleSubmissions(
    ids: number[],
    headers: AuthHeaders,
  ): Promise<void> {
    if (ids.length === 0) {
      throw new Error("No submission IDs provided");
    }

    await Promise.all(
      ids.map((id) => this.repository.archiveSubmission(id, true, headers)),
    );
  }

  async unarchiveMultipleSubmissions(
    ids: number[],
    headers: AuthHeaders,
  ): Promise<void> {
    if (ids.length === 0) {
      throw new Error("No submission IDs provided");
    }

    await Promise.all(
      ids.map((id) => this.repository.unarchiveSubmission(id, headers)),
    );
  }

  async addClassCode(
    submissionId: number,
    data: ClassCodeInput,
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (!data.class_code || !data.description) {
      throw new Error("Class code and description are required");
    }

    const response = await this.repository.addClassCode(
      submissionId,
      data,
      headers,
    );
    return this.enrichClassCode(response);
  }

  async addClassCodesBatch(
    submissionId: number,
    existingCodes: ScheduleOfHazard[],
    newCodes: ClassCodeInput[],
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard[]> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (newCodes.length === 0) {
      throw new Error("At least one class code is required");
    }

    for (const item of newCodes) {
      if (!item.class_code || !item.description) {
        throw new Error(
          "Class code and description are required for all items",
        );
      }
    }

    // Convert new codes to ScheduleOfHazard format
    const newHazards = newCodes.map((item, index) => ({
      class_code: item.class_code,
      classification: item.description,
      exposure: item.payroll || 0,
      premium_basis: item.premium_basis || "P",
      exposure_unit: "per $1000",
      location_number: item.location_number,
      hazard_number: existingCodes.length + index + 1,
    })) as ScheduleOfHazard[];

    // Combine existing + new
    const allHazards = [...existingCodes, ...newHazards];

    // PATCH with complete array
    const response = await this.repository.updateGeneralLiabilitySchedule(
      submissionId,
      allHazards,
      headers,
    );

    return response.schedule_of_hazard.map((code) =>
      this.enrichClassCode(code),
    );
  }

  async updateClassCode(
    submissionId: number,
    classCodeId: number,
    data: ClassCodeInput,
    currentCodes: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard[]> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (classCodeId < 1) {
      throw new Error("Invalid class code ID");
    }

    // PATCH with complete array (multi-rater pattern)
    const response = await this.repository.updateClassCode(
      submissionId,
      classCodeId,
      data,
      currentCodes,
      headers,
    );

    return response.schedule_of_hazard.map((code) =>
      this.enrichClassCode(code),
    );
  }

  async deleteClassCode(
    submissionId: number,
    classCodeId: number,
    currentCodes: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard[]> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (classCodeId < 1) {
      throw new Error("Invalid class code ID");
    }

    // Filter out the code to delete
    const remainingCodes = currentCodes.filter(
      (code) => code.id !== classCodeId,
    );

    // PATCH with remaining codes (multi-rater pattern)
    const response = await this.repository.deleteClassCode(
      submissionId,
      remainingCodes,
      headers,
    );

    return response.schedule_of_hazard.map((code) =>
      this.enrichClassCode(code),
    );
  }

  async updateInsuredInformation(
    submissionId: string,
    data: SubmissionUpdatePayload,
    headers: AuthHeaders,
  ): Promise<Submission> {
    const id = parseInt(submissionId, 10);
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    const submission = await this.repository.updateSubmission(id, data, headers);
    return this.enrichSubmission(submission);
  }

  async updateRenewalInformation(
    submissionId: string,
    data: RenewalInformationInput,
    headers: AuthHeaders,
  ): Promise<Submission> {
    const id = parseInt(submissionId, 10);
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    const submission = await this.repository.updateSubmission(id, data, headers);
    return this.enrichSubmission(submission);
  }

  async updateGLCoverage(
    submissionId: string,
    data: GLCoverageInput & { inferred_fields?: unknown[] },
    headers: AuthHeaders,
  ): Promise<void> {
    const id = parseInt(submissionId, 10);
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }

    await this.repository.updateGLCoverage(id, data, headers);
  }

  async updateGLFields(
    submissionId: string,
    data: Record<string, unknown>,
    headers: AuthHeaders,
  ): Promise<void> {
    const id = parseInt(submissionId, 10);
    if (id < 1) {
      throw new Error("Invalid submission ID");
    }
    await this.repository.updateGLPartial(id, data, headers);
  }

  async getAllClassCodes(): Promise<ClassCodeSearchResult[]> {
    return this.repository.getAllClassCodes();
  }

  async getAllPropertyClassCodes(): Promise<
    Array<{ code: string; description: string }>
  > {
    return this.repository.getAllPropertyClassCodes();
  }

  async searchClassCodes(
    query: string,
    limit: number = 50,
  ): Promise<ClassCodeSearchResult[]> {
    if (limit < 1 || limit > 50) {
      throw new Error("Limit must be between 1 and 50");
    }

    return this.repository.searchClassCodes(query, limit);
  }

  async createPremise(
    submissionId: number,
    data: { full_address: string; description_of_operations?: string },
    headers: AuthHeaders,
  ): Promise<PremiseData> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    return this.repository.createPremise(submissionId, data, headers);
  }

  async updatePremise(
    submissionId: number,
    premiseId: number,
    data: {
      full_address?: string;
      description_of_operations?: string;
      nature_of_business?: string;
      location_number?: number;
    },
    headers: AuthHeaders,
  ): Promise<PremiseData> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    return this.repository.updatePremise(
      submissionId,
      premiseId,
      data,
      headers,
    );
  }

  async deletePremise(
    submissionId: number,
    premiseId: number,
    headers: AuthHeaders,
  ): Promise<void> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    await this.repository.deletePremise(submissionId, premiseId, headers);
  }

  async createBuilding(
    submissionId: number,
    premiseId: number,
    data: BuildingPayload,
    headers: AuthHeaders,
  ): Promise<BuildingData> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }
    if (premiseId < 1) {
      throw new Error("Invalid premise ID");
    }

    return this.repository.createBuilding(
      submissionId,
      premiseId,
      data,
      headers,
    );
  }

  async deleteBuilding(
    submissionId: number,
    buildingId: number,
    headers: AuthHeaders,
  ): Promise<void> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }
    if (buildingId < 1) {
      throw new Error("Invalid building ID");
    }

    await this.repository.deleteBuilding(submissionId, buildingId, headers);
  }

  async updateBuilding(
    submissionId: number,
    buildingId: number,
    data: BuildingPayload,
    headers: AuthHeaders,
  ): Promise<BuildingData> {
    if (submissionId < 1) {
      throw new Error("Invalid submission ID");
    }

    if (buildingId < 1) {
      throw new Error("Invalid building ID");
    }

    return this.repository.updateBuilding(
      submissionId,
      buildingId,
      data,
      headers,
    );
  }

  // Cover letter methods

  async generateCoverLetter(
    appId: number,
    data: { carrier_id?: string; carrier_name?: string; recipient_email?: string },
  ): Promise<CoverLetter> {
    return this.repository.generateCoverLetter(appId, data);
  }

  async getCoverLetters(appId: number): Promise<CoverLetter[]> {
    return this.repository.getCoverLetters(appId);
  }

  async getCoverLetter(appId: number, clId: number): Promise<CoverLetter> {
    return this.repository.getCoverLetter(appId, clId);
  }

  async updateCoverLetter(
    appId: number,
    clId: number,
    data: {
      subject?: string;
      body_html?: string;
      recipient_email?: string;
      revision_source?: "AI_ASSIST" | "MANUAL";
      revision_id?: number;
      action?: "improve" | "formal" | "concise" | "expand";
    },
  ): Promise<CoverLetter> {
    return this.repository.updateCoverLetter(appId, clId, data);
  }

  async aiAssist(data: {
    text: string;
    action: "improve" | "formal" | "concise" | "expand";
    app_id?: number;
    cover_letter_id?: number;
  }): Promise<{ text: string }> {
    return this.repository.aiAssist(data);
  }

  async getMarketFinderUserPreferences(): Promise<MarketFinderUserPreferences> {
    return this.repository.getMarketFinderUserPreferences();
  }

  async getMarketFinderDemoConfig(): Promise<MarketFinderDemoConfigResult> {
    return this.repository.getMarketFinderDemoConfig();
  }

  async sendMarketFinderDemoAgentMessage(
    conversationId: number,
    bodyHtml: string,
  ): Promise<ConversationThreadMessage> {
    return this.repository.sendMarketFinderDemoAgentMessage(
      conversationId,
      bodyHtml,
    );
  }

  async seedMarketFinderDemoAgentThread(
    packetId: number,
  ): Promise<MarketFinderDemoAgentThreadResult> {
    return this.repository.seedMarketFinderDemoAgentThread(packetId);
  }

  async resetMarketFinderDemo(): Promise<MarketFinderDemoResetResult> {
    return this.repository.resetMarketFinderDemo();
  }

  async updateMarketFinderUserPreferences(
    data: MarketFinderUserPreferencesUpdate,
  ): Promise<MarketFinderUserPreferences> {
    return this.repository.updateMarketFinderUserPreferences(data);
  }

  async getHitRates(classCodes: string[]): Promise<HitRateResponse> {
    return this.repository.getHitRates(classCodes);
  }

  async getPackets(appId: number): Promise<MarketPacket[]> {
    return this.repository.getPackets(appId);
  }

  async createPacket(
    appId: number,
    data: { label?: string; lob: string[] },
  ): Promise<MarketPacket> {
    return this.repository.createPacket(appId, data);
  }

  async updatePacket(
    appId: number,
    packetId: number,
    data: MarketPacketUpdateInput,
  ): Promise<MarketPacket> {
    return this.repository.updatePacket(appId, packetId, data);
  }

  async deletePacket(appId: number, packetId: number): Promise<void> {
    return this.repository.deletePacket(appId, packetId);
  }

  async setPacketAttachments(
    appId: number,
    packetId: number,
    recordIds: number[],
  ): Promise<MarketPacket> {
    return this.repository.setPacketAttachments(appId, packetId, recordIds);
  }

  async setPacketRecipients(
    appId: number,
    packetId: number,
    recipients: Array<{ carrier_id: string; contact_id: number }>,
  ): Promise<MarketPacket> {
    return this.repository.setPacketRecipients(appId, packetId, recipients);
  }

  async generatePacketCoverLetter(
    appId: number,
    packetId: number,
    data: {
      current_body_html?: string;
      format?: "standard" | "concise" | "detailed";
    } = {},
  ): Promise<CoverLetter> {
    return this.repository.generatePacketCoverLetter(appId, packetId, data);
  }

  async updatePacketCoverLetter(
    appId: number,
    packetId: number,
    data: {
      subject?: string;
      body_html?: string;
      revision_source?: "AI_ASSIST" | "MANUAL";
      revision_id?: number;
      action?: string;
    },
  ): Promise<CoverLetter> {
    return this.repository.updatePacketCoverLetter(appId, packetId, data);
  }

  async createNewPacketCoverLetter(
    appId: number,
    packetId: number,
  ): Promise<CoverLetter> {
    return this.repository.createNewPacketCoverLetter(appId, packetId);
  }

  async getPacketCoverLetterRevisions(
    appId: number,
    packetId: number,
  ): Promise<CoverLetterRevision[]> {
    return this.repository.getPacketCoverLetterRevisions(appId, packetId);
  }

  async previewPacket(appId: number, packetId: number): Promise<MarketPacketPreview> {
    return this.repository.previewPacket(appId, packetId);
  }

  async sendPacket(appId: number, packetId: number): Promise<MarketPacketSendResult> {
    return this.repository.sendPacket(appId, packetId);
  }

  async sendPacketAgentEmail(
    appId: number,
    packetId: number,
    data: AgentEmailInput,
  ): Promise<ConversationThread> {
    return this.repository.sendPacketAgentEmail(appId, packetId, data);
  }

  async uploadPacketAgentEmailAttachments(
    appId: number,
    packetId: number,
    formData: FormData,
  ): Promise<ConversationReplyAttachmentRef[]> {
    return this.repository.uploadPacketAgentEmailAttachments(
      appId,
      packetId,
      formData,
    );
  }

  async getCommunicationsSummary(
    underwriterIds?: string[],
  ): Promise<CommunicationsSummary> {
    return this.repository.getCommunicationsSummary(underwriterIds);
  }

  async listApplicationConversations(
    appId: number,
  ): Promise<ConversationThread[]> {
    return this.repository.listApplicationConversations(appId);
  }

  async listConversationMessages(
    appId: number,
    conversationId: number,
  ): Promise<ConversationThreadMessage[]> {
    return this.repository.listConversationMessages(appId, conversationId);
  }

  async getConversationMessageEmail(
    appId: number,
    messageId: number,
  ): Promise<ConversationMessageEmail> {
    return this.repository.getConversationMessageEmail(appId, messageId);
  }

  async listPossibleMatches(appId: number): Promise<PossibleMatch[]> {
    return this.repository.listPossibleMatches(appId);
  }

  async acceptPossibleMatch(
    appId: number,
    messageId: number,
    conversationId: number,
  ): Promise<void> {
    return this.repository.acceptPossibleMatch(appId, messageId, conversationId);
  }

  async rejectPossibleMatch(appId: number, messageId: number): Promise<void> {
    return this.repository.rejectPossibleMatch(appId, messageId);
  }

  async markConversationMessageRead(
    appId: number,
    messageId: number,
  ): Promise<ConversationThreadMessage> {
    return this.repository.markConversationMessageRead(appId, messageId);
  }

  async detachEmailFromConversation(
    appId: number,
    messageId: number,
  ): Promise<void> {
    return this.repository.detachEmailFromConversation(appId, messageId);
  }

  async attachEmailToConversation(
    appId: number,
    messageId: number,
    conversationId: number,
  ): Promise<void> {
    return this.repository.attachEmailToConversation(
      appId,
      messageId,
      conversationId,
    );
  }

  async replyToConversation(
    appId: number,
    conversationId: number,
    bodyHtml: string,
    attachments: ConversationReplyAttachmentRef[] = [],
    targetMessageId?: number,
  ): Promise<ConversationThreadMessage> {
    return this.repository.replyToConversation(
      appId,
      conversationId,
      bodyHtml,
      attachments,
      targetMessageId,
    );
  }

  async uploadConversationReplyAttachments(
    appId: number,
    conversationId: number,
    formData: FormData,
  ): Promise<ConversationReplyAttachmentRef[]> {
    return this.repository.uploadConversationReplyAttachments(appId, conversationId, formData);
  }

  async listCommunicationReviewMessages(options: {
    archived?: boolean;
    page?: number;
    perPage?: number;
    search?: string;
    filters?: string;
  }): Promise<CommunicationReviewPage> {
    return this.repository.listCommunicationReviewMessages(options);
  }

  async getCommunicationReviewMessage(
    messageId: number,
  ): Promise<ConversationReviewMessage> {
    return this.repository.getCommunicationReviewMessage(messageId);
  }

  async resolveCommunicationReviewMessage(
    messageId: number,
    conversationId: number,
  ): Promise<ConversationReviewMessage> {
    return this.repository.resolveCommunicationReviewMessage(messageId, conversationId);
  }

  async resolveCommunicationReviewMessageByReference(
    messageId: number,
    conversationReference: string,
  ): Promise<ConversationReviewMessage> {
    return this.repository.resolveCommunicationReviewMessageByReference(
      messageId,
      conversationReference,
    );
  }

  async dismissCommunicationReviewMessage(
    messageId: number,
  ): Promise<void> {
    return this.repository.dismissCommunicationReviewMessage(messageId);
  }

  async archiveCommunicationReviewMessage(
    messageId: number,
  ): Promise<void> {
    return this.repository.archiveCommunicationReviewMessage(messageId);
  }

  async unarchiveCommunicationReviewMessage(
    messageId: number,
  ): Promise<void> {
    return this.repository.unarchiveCommunicationReviewMessage(messageId);
  }

  async getCommunicationReviewMessageEmail(
    messageId: number,
  ): Promise<ConversationMessageEmail> {
    return this.repository.getCommunicationReviewMessageEmail(messageId);
  }

  async getMarkets(
    lob?: string,
    includeInactive?: boolean,
  ): Promise<MarketDirectoryItem[]> {
    return this.repository.getMarkets(lob, includeInactive);
  }

  async updateMarket(
    carrierId: string,
    data: MarketUpdateInput,
  ): Promise<MarketDirectoryItem> {
    return this.repository.updateMarket(carrierId, data);
  }

  async getMarketsDirectory(
    params: MarketsDirectoryParams,
  ): Promise<MarketsDirectoryResponse> {
    return this.repository.getMarketsDirectory(params);
  }

  async setContactActive(contactId: number, archive: boolean): Promise<MarketContact> {
    return this.repository.setContactActive(contactId, archive);
  }

  async createMarket(data: MarketCreateInput): Promise<MarketCreateResponse> {
    return this.repository.createMarket(data);
  }

  async uploadMarketImage(
    carrierId: string,
    file: File,
  ): Promise<MarketDirectoryItem> {
    return this.repository.uploadMarketImage(carrierId, file);
  }

  async deleteMarketImage(carrierId: string): Promise<MarketDirectoryItem> {
    return this.repository.deleteMarketImage(carrierId);
  }

  async getMarketContacts(carrierId: string): Promise<MarketContact[]> {
    return this.repository.getMarketContacts(carrierId);
  }

  async getMarketContactsDirectory(
    params: MarketContactDirectoryParams,
  ): Promise<MarketContactDirectoryResponse> {
    return this.repository.getMarketContactsDirectory(params);
  }

  async createMarketContact(
    carrierId: string,
    data: MarketContactInput,
  ): Promise<MarketContact> {
    return this.repository.createMarketContact(carrierId, data);
  }

  async updateMarketContact(
    contactId: number,
    data: MarketContactInput,
  ): Promise<MarketContact> {
    return this.repository.updateMarketContact(contactId, data);
  }

  async getMarketFinderMe(): Promise<MarketFinderActorInfo> {
    return this.repository.getMarketFinderMe();
  }

  async searchAimCompanies(q: string, page = 0, size = 20): Promise<AimCompanyPage> {
    return this.repository.searchAimCompanies(q, page, size);
  }

  async getAimCompanyContacts(
    referenceId: number,
    q = "",
    page = 0,
    size = 20,
    carrierId?: string,
  ): Promise<AimContactPage> {
    return this.repository.getAimCompanyContacts(referenceId, q, page, size, carrierId);
  }

  async importAimMarket(data: AimMarketImportInput): Promise<MarketCreateResponse> {
    return this.repository.importAimMarket(data);
  }

  async importAimContacts(
    carrierId: string,
    data: AimContactImportInput,
  ): Promise<AimContactImportResult> {
    return this.repository.importAimContacts(carrierId, data);
  }

  private enrichSubmission(submission: Submission): Submission {
    return {
      ...submission,
      effective_date: new Date(submission.effective_date),
      created_at: new Date(submission.created_at),
      updated_at: new Date(submission.updated_at),
      archived_at: submission.archived_at
        ? new Date(submission.archived_at)
        : null,
      received_date: submission.received_date
        ? new Date(submission.received_date)
        : null,
    };
  }

  private enrichClassCode(classCode: ScheduleOfHazard): ScheduleOfHazard {
    return {
      ...classCode,
      location_number: classCode.location_number || 1,
      hazard_number: classCode.hazard_number || 0,
      exposure: classCode.exposure || 0,
      premium_basis: classCode.premium_basis || "Payroll",
      exposure_unit: classCode.exposure_unit || null,
    };
  }
}

// Export singleton instances for direct use
export const submissionsService = SubmissionsService.getInstance();
export const marketFinderSubmissionsService =
  SubmissionsService.getMarketFinderInstance();
